<script>
import {Link} from "@inertiajs/vue3";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import IconHeart from "@js/components/Icons/IconHeart.vue";
import IconHeartFilled from "@js/components/Icons/IconHeartFilled.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import {useLikeStore} from "@js/store/LikeStore.js";
import {useCompareStore} from "@js/store/CompareStore.js";
import {useCartStore} from "@js/store/CartStore.js";
import IconCheckMark from "@js/components/Icons/IconCheckMark.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import {computed} from "vue";
import CartQuantityStepper from "@js/components/Sections/Cart/Component/CartQuantityStepper.vue";
import {useCatalogStore} from "@js/store/CatalogStore.js";
import IconInfo from "@js/components/Icons/IconInfo.vue";
import IconTrash from "@js/components/Icons/IconTrash.vue";
import {useProductStore} from "@js/store/ProductStore.js";
import {ecomClick, mapProduct} from "@js/ecommerce.js";

export default {
    name: "CatalogProductItemWide",
    components: {
        IconTrash,
        IconInfo,
        CartQuantityStepper,
        OnlyMobile,
        OnlyDesktop,
        IconCheckMark,
        NumberFormatComponent,
        IconHeart,
        IconHeartFilled,
        ImageLoader,
        Link,
    },
    props: {
        data: {type: Object, required: true},
        cart_mode: {type: Boolean, default: false},
        // оставляем для совместимости, но реально режимов больше нет
        defaultMode: {type: String, default: "t"},
        listName: {type: String, default: 'Каталог'},
        position: {type: Number, default: 1},
    },
    methods: {
        trackClick() {
            try {
                ecomClick(mapProduct(this.data, {list: this.listName, position: this.position}))
            } catch (e) {
            }
        }
    },
    setup(props) {
        const likeStore = useLikeStore();
        const compareStore = useCompareStore();
        const cartStore = useCartStore();
        const catalogStore = useCatalogStore();
        const productStore = useProductStore();

        // Новый проект:
        // - цена одна: data.price (за тонну)
        // - режим всегда 't'
        cartStore.registerCatalogProduct(props.data, 't');

        const qtyProxy = computed({
            get: () => cartStore.uiOf(props.data.id).qty,
            set: (val) => cartStore.setQtyFromInput(props.data.id, val),
        });

        const unitText = computed(() => props.data.price_type === 'kg' ? '/кг' : '/тонна');

        const hasAnyPrice = computed(() => Number(props.data?.price ?? 0) > 0);

        const canBuy = computed(
            () => props.data.available && cartStore.priceActive(props.data.id) > 0
        );

        const imageSrc = computed(() => {
            // 1) если бек отдал готовые URL (наш новый формат)
            const u = props.data?.image_urls?.[0]
            if (u) return u

            // 2) если пришли только имена файлов (fallback)
            const f = props.data?.images?.[0]
            if (f) return `/storage/products/${encodeURIComponent(f)}`

            // 3) заглушка
            return '/images/no-image.svg'
        })

        return {
            catalogStore,
            likeStore,
            compareStore,
            productStore,
            cartStore,
            qtyProxy,
            unitText,
            hasAnyPrice,
            canBuy,
            imageSrc
        };
    },
};
</script>

<template>
    <div class="relative w-full bg-white overflow-hidden"
         :class="[cart_mode ? '' : 'border border-gray-200 rounded-2xl']">
        <!-- ============ MOBILE (<=1024) ============ -->
        <OnlyMobile :maxSize="1024">
            <div class="p-3">
                <div class="grid grid-cols-[100px_1fr] gap-3 items-start">
                    <!-- Фото + бейджи -->
                    <div class="relative">
                        <Link :href="route('product', data.slug)" @pointerdown="trackClick"
                              class="block w-[100px] h-auto rounded-lg border border-gray-200 overflow-hidden">
                            <ImageLoader
                                :title="data.title"
                                :src="imageSrc"
                                class="w-full h-full"
                                imgClasses="w-full h-full object-cover"/>
                        </Link>

                        <div class="absolute left-0 top-0 flex gap-1">
                            <span v-if="data.is_promo"
                                  class="rounded bg-amber-400 px-1.5 py-[1px] text-[10px] font-medium text-black">Акция</span>
                            <span v-if="data.is_hit"
                                  class="rounded bg-green-600 px-1.5 py-[1px] text-[10px] font-medium text-white">Хит</span>
                        </div>
                    </div>

                    <!-- Заголовок + метки + лайк/сравнение -->
                    <div class="min-w-0">
                        <div class="flex items-start justify-between gap-2">
                            <div class="w-min text-nowrap text-[10px] px-1.5 py-[2px] rounded"
                                 :class="[data.available ? 'bg-emerald-800 text-white': 'bg-amber-400 text-black']">
                                {{ data.available ? 'Принимаем' : 'Временно не принимаем' }}
                            </div>

                            <div class="flex shrink-0 gap-1">
                                <!--                                <button v-if="!cart_mode"-->
                                <!--                                        class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white"-->
                                <!--                                        @click.prevent="compareStore.toggleCompare(data.id)"-->
                                <!--                                        :aria-pressed="compareStore.isCompared(data.id)"-->
                                <!--                                        :title="compareStore.isCompared(data.id) ? 'Убрать из сравнения' : 'В сравнение'">-->
                                <!--                                    <IconScales-->
                                <!--                                        :class="['w-4 h-4', compareStore.isCompared(data.id) ? 'text-blue-500' : 'text-gray-700']"/>-->
                                <!--                                </button>-->
                                <button v-if="!cart_mode"
                                        class="inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white"
                                        @click.prevent="likeStore.toggleLike(data.id)"
                                        :aria-pressed="likeStore.isLiked(data.id)"
                                        :title="likeStore.isLiked(data.id) ? 'Убрать из избранного' : 'В избранное'">
                                    <IconHeartFilled v-if="likeStore.isLiked(data.id)" class="w-4 h-4 text-likeColor"/>
                                    <IconHeart v-else class="w-4 h-4 text-gray-700"/>
                                </button>
                                <button v-if="cart_mode"
                                        class="group inline-flex h-8 w-8 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition"
                                        @click.prevent="cartStore.remove(data.id, 't')"
                                        title="Удалить">
                                    <IconTrash class="h-4 w-4 text-gray-700 md:group-hover:text-likeColor transition"/>
                                </button>
                            </div>
                        </div>

                        <Link :href="route('product', data.slug)" @pointerdown="trackClick"
                              class="my-1.5 block text-sm leading-[16px] font-medium text-gray-900 hover:text-navHoverColor transition line-clamp-2">
                            {{ data.title }}
                        </Link>
                        <div v-if="data.sub_category"
                             class="text-nowrap w-min rounded text-black bg-grayBg px-2 py-1 text-[11px] font-medium">
                            {{ data.sub_category }}
                        </div>
                    </div>
                </div>

                <!-- Цена -->
                <div class="mt-2">
                    <template v-if="hasAnyPrice">
                        <div class="flex items-center justify-between gap-2">
                            <div></div>
                            <div class="text-right">
                                <div class="text-[12px] text-gray-700 leading-tight">
                                    <NumberFormatComponent
                                        :number="cartStore.priceActive(data.id)"
                                        prepend_text="Цена: "
                                        append_text=" ₽"
                                        class="text-sm font-medium text-black"/>
                                    <span class="text-[11px] font-medium">{{ unitText }}</span>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="font-bold leading-tight">Цена договорная</div>
                    </template>
                </div>

                <!-- Количество + итого -->
                <div v-if="canBuy" class="mt-2">
                    <div class="flex items-center justify-between gap-5">
                        <CartQuantityStepper v-model="qtyProxy"
                                             :min="cartStore.minQty(data.id)"
                                             :step="cartStore.step(data.id)"
                                             :disabled="!canBuy"
                                             size="sm"
                                             width-class="w-[112px]"
                                             @inc="cartStore.incQty(data.id)"
                                             @dec="cartStore.decQty(data.id)"/>
                        <NumberFormatComponent :number="cartStore.totalPrice(data.id)"
                                               prepend_text="Итого: "
                                               append_text=" ₽"
                                               class="text-sm font-medium"/>
                    </div>
                </div>

                <div v-if="!cart_mode" class="mt-3">
                    <template v-if="!canBuy">
                        <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                                class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-amber-400 text-sm font-semibold text-black transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50">
                            Узнать цену
                        </button>
                    </template>
                    <template v-else>
                        <div class="flex gap-2">
                            <template v-if="cartStore.isInCartExact(data.id)">
                                <Link :href="route('cart')"
                                      class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition hover:bg-emerald-500">
                                    <IconCheckMark class="w-5 h-5"/>
                                    Уже в расчёте
                                </Link>
                            </template>
                            <template v-else>
                                <button
                                    class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-sm font-semibold text-black transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                                    :disabled="!canBuy"
                                    @click="cartStore.addToCartFromCard(data.id)">
                                    Добавить в расчёт
                                </button>
                            </template>
                            <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                                    class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-black/10 border-black/10 text-sm font-semibold text-black transition md:hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">
                                Сдать сейчас
                            </button>
                        </div>
                    </template>
                </div>
            </div>
        </OnlyMobile>

        <!-- ============ DESKTOP (как было) ============ -->
        <OnlyDesktop :maxSize="1024">
            <div class="p-3 md:p-4">
                <div class="flex h-full gap-4 flex-col md:flex-row md:gap-6">
                    <!--                    {{data}}-->
                    <!-- Картинка + бейджи -->
                    <Link :href="route('product', data.slug)" @pointerdown="trackClick"
                          class="relative w-full md:w-40">
                        <div class="overflow-hidden w-full rounded-xl border border-gray-200">
                            <ImageLoader :title="data.title"
                                         :src="imageSrc"
                                         imgClasses="w-full h-full object-contain"/>
                        </div>
                        <div class="absolute left-0 top-0 flex gap-1">
                            <span v-if="data.is_promo"
                                  class="rounded bg-amber-400 px-2 py-0.5 text-[11px] font-medium text-black">Акция</span>
                            <span v-if="data.is_hit"
                                  class="rounded bg-green-600 px-2 py-0.5 text-[11px] font-medium text-white">Хит</span>
                        </div>
                    </Link>

                    <!-- Центр -->
                    <div class="flex min-w-0 grow flex-col gap-1 py-0">
                        <div class="w-min text-nowrap text-[11px] px-2 py-1 rounded leading-none"
                             :class="[data.available ? 'bg-emerald-800 text-white': 'bg-amber-400 text-black']">
                            {{ data.available ? 'Принимаем' : 'Временно не принимаем' }}
                        </div>
                        <Link :href="route('product', data.slug)" @pointerdown="trackClick"
                              class="max-w-[400px] md:line-clamp-2 text-sm md:text-base font-medium leading-5 text-gray-900 hover:text-navHoverColor transition">
                            {{ data.title }}
                        </Link>
                        <div v-if="data.sub_category"
                             class="text-nowrap w-min rounded text-black bg-grayBg px-2 py-1 text-[11px] font-medium">
                            {{ data.sub_category }}
                        </div>

                        <div class="mt-auto">
                            <div v-if="hasAnyPrice" class="flex flex-col gap-1.5">
                                <div class="text-sm text-gray-700">
                                    <NumberFormatComponent
                                        :number="cartStore.priceActive(data.id)"
                                        prepend_text="Цена: "
                                        append_text=" ₽"
                                        class="text-base font-semibold text-black"/>
                                    <span class="font-medium text-sm">{{ unitText }}</span>
                                </div>
                            </div>
                            <div v-else class="font-medium leading-tight">
                                Цена договорная
                            </div>
                        </div>
                    </div>

                    <!-- Правая колонка -->
                    <div class="flex flex-col items-stretch mt-auto gap-3 pt-0 w-full md:w-[360px]">
                        <div v-if="canBuy" class="flex max-md:flex-col gap-3"
                             :class="[cart_mode ? 'flex-col items-end' : 'md:items-center']">
                            <CartQuantityStepper v-model="qtyProxy"
                                                 :min="cartStore.minQty(data.id)"
                                                 :step="cartStore.step(data.id)"
                                                 :disabled="!canBuy"
                                                 size="md"
                                                 width-class="w-32"
                                                 @inc="cartStore.incQty(data.id)"
                                                 @dec="cartStore.decQty(data.id)"
                                                 :class="{'order-2': cart_mode}"/>
                            <div class="text-base font-semibold text-gray-900"
                                 :class="{'order-1': cart_mode}">
                                <NumberFormatComponent :number="cartStore.totalPrice(data.id)"
                                                       prepend_text="Итого: "
                                                       append_text=" ₽"/>
                            </div>
                        </div>

                        <template v-if="!cart_mode">
                            <template v-if="!canBuy">
                                <div class="flex flex-col gap-2">
                                    <div class="rounded bg-grayBg px-2 py-2 text-xs leading-tight font-medium">
                                        <p>Оставьте контакты — подберём цену под вашу задачу. Зафиксируем стоимость на
                                            сутки.</p>
                                    </div>
                                    <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                                            class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-amber-400 text-base font-semibold text-black transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50">
                                        Узнать цену
                                    </button>
                                </div>
                            </template>
                            <template v-else>
                                <div class="flex gap-3">
                                    <template v-if="cartStore.isInCartExact(data.id)">
                                        <Link :href="route('cart')"
                                              class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-sm font-semibold text-white transition hover:bg-emerald-500">
                                            <IconCheckMark class="w-5 h-5"/>
                                            Уже в расчёте
                                        </Link>
                                    </template>
                                    <template v-else>
                                        <button
                                            class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-sm font-semibold text-black transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                                            :disabled="!canBuy"
                                            @click="cartStore.addToCartFromCard(data.id)">
                                            Добавить в расчёт
                                        </button>
                                    </template>
                                    <button @click.prevent="productStore.showFastBuyPopup(data.id)"
                                            class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-black/10 border-black/10 text-sm font-semibold text-black transition md:hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50">
                                        Сдать сейчас
                                    </button>
                                </div>
                            </template>
                        </template>
                    </div>

                    <!-- Избранное/Сравнение/Удалить -->
                    <div class="absolute flex gap-2 end-4 top-4">
                        <!--                        <button v-if="!cart_mode"-->
                        <!--                                class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"-->
                        <!--                                @click.prevent="compareStore.toggleCompare(data.id)"-->
                        <!--                                :aria-pressed="compareStore.isCompared(data.id)"-->
                        <!--                                :title="compareStore.isCompared(data.id) ? 'Убрать из сравнения' : 'В сравнении'">-->
                        <!--                            <IconScales v-if="compareStore.isCompared(data.id)"-->
                        <!--                                        class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-blue-500"/>-->
                        <!--                            <IconScales v-else-->
                        <!--                                        class="min-w-5 min-h-5 max-w-5 max-h-5 mb-[3px] text-gray-700 group-hover:text-blue-500 transition"/>-->
                        <!--                        </button>-->

                        <button v-if="!cart_mode"
                                class="group inline-flex h-9 w-9 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                                @click.prevent="likeStore.toggleLike(data.id)"
                                :aria-pressed="likeStore.isLiked(data.id)"
                                :title="likeStore.isLiked(data.id) ? 'Убрать из избранного' : 'В избранное'">
                            <IconHeartFilled v-if="likeStore.isLiked(data.id)" class="h-5 w-5 text-likeColor"/>
                            <IconHeart v-else class="h-5 w-5 text-gray-700 group-hover:text-likeColor transition"/>
                        </button>

                        <button v-if="cart_mode"
                                class="group inline-flex h-7 w-7 shadow-sm items-center justify-center rounded-full border border-gray-200 bg-white hover:bg-gray-50"
                                @click.prevent="cartStore.remove(data.id, 't')"
                                title="Удалить">
                            <IconTrash class="h-3.5 w-3.5 text-gray-700 md:group-hover:text-likeColor transition"/>
                        </button>
                    </div>
                </div>
            </div>
        </OnlyDesktop>
    </div>
</template>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    -webkit-line-clamp: 2;
}

/* Чуть плотнее кнопки/контролы на мобильном */
:deep(.segmented-sm) {
    font-size: 12px
}
</style>
